import type { Tag } from "@repo/types";
import { DocumentSortableItem, SortableList } from ".";
import type { SortableListProps } from "./SortableList";
import { useState } from "react";
import { FormDialog, type FormDialogFormProps } from "../dialog/FormDialog";
import { DocumentForm } from "../documents/DocumentForm";
import { useTenantStore } from "@/stores/tenantStore";
import { AgendaItemForm } from "../agendaItems/AgendaItemForm";
import {
  AgendaItemSortableItem,
  AgendaModuleSortableItem,
} from "./SortableItems";
import { AgendaModuleForm } from "../agendaModules/AgendaModuleForm";
import { Chip } from "@repo/ui/components/Base/Chip";
import {
  AGENDA_ITEM_MOTIF,
  AGENDA_MODULE_MOTIF,
  DOCUMENT_MOTIF,
} from "@/lib/constants";
import type { DocumentDto } from "@/repositories/documentRepo";
import type { AgendaItemDto } from "@/repositories/agendaItemRepo";
import type { AgendaModuleDto } from "@/repositories/agendaModuleRepo";

const renderTags = (item: { id: string; tags?: Tag[] }) => (
  <div
    key={"tags-" + item.id}
    className="w-0 grow-[1.5] overflow-hidden flex items-center"
  >
    <span className="block mr-2">Tags:</span>
    <div className="flex flex-wrap">
      {item.tags?.map((tag) => <Chip key={tag.id}>{tag.text}</Chip>)}
    </div>
  </div>
);

interface BaseSortableListProps<T>
  extends Pick<
    SortableListProps<T>,
    | "items"
    | "onChange"
    | "editableLabel"
    | "notEditableLabel"
    | "deleteLabel"
    | "disabled"
  > {}

export const DocumentSortableList = ({
  items,
  onChange,
  ...props
}: BaseSortableListProps<DocumentDto>) => {
  const tenantId = useTenantStore((s) => s.id);
  const [editItem, setEditItem] = useState<DocumentDto>();
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (item: DocumentDto) => {
    setEditItem(item);
    setEditOpen(true);
  };

  return (
    <>
      <FormDialog
        open={editOpen}
        setOpen={setEditOpen}
        title="Edit Document"
        preventDefault
        showTrigger={false}
        headerClassName={DOCUMENT_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <DocumentForm
            tenantId={tenantId}
            documentFormData={editItem}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            withinDialog
          />
        )}
      />
      <SortableList<DocumentDto>
        items={items.map((item) => ({ ...item, isEditable: true }))}
        onChange={onChange}
        onEdit={handleEdit}
        sortableItem={DocumentSortableItem}
        renderTags={renderTags}
        {...props}
      />
    </>
  );
};

export const AgendaItemSortableList = ({
  items,
  onChange,
  ...props
}: BaseSortableListProps<AgendaItemDto>) => {
  const tenantId = useTenantStore((s) => s.id);
  const [editItem, setEditItem] = useState<AgendaItemDto>();
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (item: AgendaItemDto) => {
    setEditItem(item);
    setEditOpen(true);
  };

  return (
    <>
      <FormDialog
        open={editOpen}
        setOpen={setEditOpen}
        title="Edit Agenda Item"
        preventDefault
        showTrigger={false}
        headerClassName={AGENDA_ITEM_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <AgendaItemForm
            tenantId={tenantId}
            agendaItemFormData={editItem}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            withinDialog
          />
        )}
      />
      <SortableList<AgendaItemDto>
        items={items.map((item) => ({ ...item, isEditable: true }))}
        onChange={onChange}
        onEdit={handleEdit}
        sortableItem={AgendaItemSortableItem}
        renderTags={renderTags}
        {...props}
      />
    </>
  );
};

export const AgendaModuleSortableList = ({
  items,
  onChange,
  isTemplate = false,
  ...props
}: BaseSortableListProps<AgendaModuleDto> & {
  isTemplate?: boolean;
}) => {
  const tenantId = useTenantStore((s) => s.id);
  const [editItem, setEditItem] = useState<AgendaModuleDto>();
  const [editOpen, setEditOpen] = useState(false);

  const handleEdit = (item: AgendaModuleDto) => {
    setEditItem(item);
    setEditOpen(true);
  };
  const handleSubmit = (newItem: AgendaModuleDto) => {
    const newItems = items.map((item) =>
      item.id === newItem.id ? newItem : item
    );
    onChange(newItems);
  };

  return (
    <>
      <FormDialog
        open={editOpen}
        setOpen={setEditOpen}
        title="Edit Agenda Section"
        preventDefault
        showTrigger={false}
        headerClassName={AGENDA_MODULE_MOTIF}
        usesWithinDialog
        form={({
          handleCancel = () => {},
          handleComplete = () => {},
        }: FormDialogFormProps) => (
          <AgendaModuleForm
            isTemplate={isTemplate}
            tenantId={tenantId}
            agendaModuleFormData={editItem}
            handleCancel={handleCancel}
            handleComplete={handleComplete}
            onSubmit={handleSubmit}
            withinDialog
          />
        )}
      />
      <SortableList<AgendaModuleDto>
        items={items}
        onChange={onChange}
        onEdit={handleEdit}
        sortableItem={AgendaModuleSortableItem}
        renderTags={renderTags}
        {...props}
      />
    </>
  );
};
